// RoastLog - Yoshan DY Series Modbus RTU Device
//
// Hardware: Omron E5CC PID controllers connected via RS-485
// Connection: Modbus RTU over USB or Bluetooth serial (HC-02 module)
// Protocol: 9600 baud, 8 data bits, no parity, 1 stop bit
//
// Register Map (from Artisan DY.aset):
//   Register 0-1: Bean Temperature (float32, big-endian, °C)
//   Register 2-3: Environmental Temperature (float32, big-endian, °C)
//
// Data Encoding:
//   - 32-bit IEEE 754 float, big-endian byte order, standard word order
//   - Temperatures in Celsius (no conversion needed)
//   - Function code 3 (Read Holding Registers)
//   - Slave ID: 1

import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { yoshanRTUSettings } from '$lib/PersistedState.svelte';
import { modbusDebugEnabled } from '$lib/State.svelte';
import type { SerialPortInfo } from '$lib/types/Device';
import { invoke } from '@tauri-apps/api/core';
import { BaseDevice } from '../base';

class RTUClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RTUClientError';
  }
}

interface YoshanRTUReading {
  bean: number;
  environment: number;
}

export class YoshanRTUDevice extends BaseDevice {
  type: DeviceType = DeviceType.YOSHAN_RTU;
  private portName: string = '';
  private slaveId: number = 1;

  // Yoshan requires a slower polling interval (2 seconds) to avoid
  // Modbus communication errors, especially during gas ignition (EMI).
  // This matches the Artisan config Delay=2000.
  protected pollingInterval: number = 2000;

  // Register mappings for Omron E5CC PIDs
  // Each temperature spans two 16-bit registers (32-bit IEEE 754 float)
  private static readonly REGISTERS = {
    BT_REGISTER: 0, // Bean Temperature (registers 0-1)
    ET_REGISTER: 2 // Environmental Temperature (registers 2-3)
  };

  protected async onConnect(): Promise<boolean> {
    this.portName = yoshanRTUSettings.current.portName || '';
    this.slaveId = yoshanRTUSettings.current.slaveId || 1;

    try {
      if (!this.portName || this.portName.trim() === '') {
        logger.error('Port name is not set. Cannot connect to Yoshan RTU device.');
        return false;
      }

      logger.info(`Attempting to connect to Yoshan RTU device on port: ${this.portName}`);

      // All 4 registers for background monitoring (2 floats = 4 registers)
      const registersToMonitor = [
        YoshanRTUDevice.REGISTERS.BT_REGISTER, // 0 - Bean Temperature LSW
        YoshanRTUDevice.REGISTERS.BT_REGISTER + 1, // 1 - Bean Temperature MSW
        YoshanRTUDevice.REGISTERS.ET_REGISTER, // 2 - Environmental Temperature LSW
        YoshanRTUDevice.REGISTERS.ET_REGISTER + 1 // 3 - Environmental Temperature MSW
      ];

      const connectParams = {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        registersToMonitor
      };

      logger.debug('Yoshan RTU connect parameters:', connectParams);

      await invoke('connect_rtu', connectParams);

      logger.info(
        `Connected to Yoshan RTU device on ${this.portName} (slave ${this.slaveId}), monitoring registers: ${registersToMonitor.join(', ')}`
      );
      return true;
    } catch (error) {
      logger.error(`Failed to connect to Yoshan RTU device: ${error}`);
      return false;
    }
  }

  protected onDisconnect(): void {
    invoke('disconnect_rtu', {
      portPath: this.portName,
      baudRate: 9600,
      slaveId: this.slaveId
    }).catch((error) => logger.warn(`Yoshan RTU disconnect error: ${error}`));
  }

  protected async updateReadings(): Promise<void> {
    try {
      const readings = await this.readAllValues();

      if (modbusDebugEnabled.current === true) {
        logger.debug('DEVICE_YOSHAN_RTU_READINGS', { readings });
      }

      // Yoshan reports temperatures in Celsius — no conversion needed
      this.localReadings = [
        readings.bean, // TC1: Bean Temperature
        this.tcSettings?.[1].active ? readings.environment : null, // TC2: Environmental
        null, // TC3: Not used
        null // TC4: Not used
      ];
    } catch (error) {
      if (error instanceof RTUClientError) {
        logger.error(`RTU error: ${error.message}`);
      } else {
        logger.error(`Unknown error: ${error}`);
      }
      throw error;
    }
  }

  /**
   * Handle errors during polling. Yoshan roasters experience Modbus
   * communication errors during gas ignition due to EMI. Log and retry
   * on next poll rather than disconnecting (overrides base class which
   * calls stopTimer on error).
   */
  protected handleError(error: unknown): void {
    logger.warn(`Yoshan RTU polling error (will retry): ${error}`);
  }

  private async readAllValues(): Promise<YoshanRTUReading> {
    try {
      logger.debug('Reading all values from Yoshan RTU device');

      const [beanReading, environmentReading] = await Promise.all([
        this.readFloatRegisterRTU(YoshanRTUDevice.REGISTERS.BT_REGISTER),
        this.readFloatRegisterRTU(YoshanRTUDevice.REGISTERS.ET_REGISTER)
      ]);

      logger.debug('Read all values from Yoshan RTU device');

      return {
        bean: beanReading,
        environment: environmentReading
      };
    } catch (err: any) {
      const regex = /RTU connection not established/i;
      if (regex.test(err)) {
        throw new RTUClientError('Could not establish RTU connection');
      } else {
        logger.warn(`Yoshan RTU reading error: ${err}`);
      }

      return {
        bean: 0,
        environment: 0
      };
    }
  }

  /**
   * Read a 32-bit float from two consecutive registers, using cached values when available.
   */
  private async readFloatRegisterRTU(startRegister: number): Promise<number> {
    try {
      // Try cached values from background read loop first
      const cachedValues = await this.getCachedRegisterValues([startRegister, startRegister + 1]);

      if (
        cachedValues.size === 2 &&
        cachedValues.has(startRegister) &&
        cachedValues.has(startRegister + 1)
      ) {
        const reg1 = cachedValues.get(startRegister)!;
        const reg2 = cachedValues.get(startRegister + 1)!;

        if (modbusDebugEnabled.current === true) {
          const hexValues = YoshanRTUDevice.formatRegistersHex(reg1, reg2);
          logger.debug(
            `Using cached values for register ${startRegister}: reg1=${reg1}, reg2=${reg2} (hex: ${hexValues})`
          );
        }

        return YoshanRTUDevice.convertToFloat(reg1, reg2);
      }

      // Fallback to direct read if no cached values available
      if (modbusDebugEnabled.current === true) {
        logger.debug(
          `No cached values available for register ${startRegister}, performing direct read`
        );
      }

      const results: number[] = await invoke('read_rtu', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        reg: startRegister,
        count: 2
      });

      if (modbusDebugEnabled.current === true) {
        const hexValues = YoshanRTUDevice.formatRegistersHex(results[0], results[1]);
        logger.debug(
          `Direct read values for register ${startRegister}: reg1=${results[0]}, reg2=${results[1]} (hex: ${hexValues})`
        );
      }

      return YoshanRTUDevice.convertToFloat(results[0], results[1]);
    } catch (error) {
      logger.error(`Failed to read Yoshan RTU register ${startRegister}: ${error}`);
      throw error;
    }
  }

  /**
   * Alternative method using direct temperature reading (bypasses cache).
   */
  private async readTemperatureRTU(register: number): Promise<number> {
    try {
      const results: number[] = await invoke('read_rtu', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        reg: register,
        count: 2
      });

      return YoshanRTUDevice.convertToFloat(results[0], results[1]);
    } catch (error) {
      logger.error(`Failed to read RTU temperature from register ${register}: ${error}`);
      throw error;
    }
  }

  /**
   * Get cached register values from the background read loop.
   */
  private async getCachedRegisterValues(registers: number[]): Promise<Map<number, number>> {
    try {
      const result = (await invoke('get_rtu_latest_values', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        registers
      })) as Record<number, number>;

      const cachedValues = new Map<number, number>();
      for (const [reg, value] of Object.entries(result)) {
        cachedValues.set(parseInt(reg), value);
      }
      return cachedValues;
    } catch (error) {
      logger.debug(`Failed to get cached register values: ${error}`);
      return new Map();
    }
  }

  /**
   * Convert two 16-bit registers to a 32-bit IEEE 754 float.
   *
   * Big-endian byte order, standard (big-endian) word order.
   * Matches Artisan config: littleEndianFloats=false, wordorderLittle=false
   *
   * reg1 = high word (bits 31:16), reg2 = low word (bits 15:0)
   */
  static convertToFloat(reg1: number, reg2: number): number {
    const buffer = new ArrayBuffer(4);
    const view = new DataView(buffer);

    view.setUint16(0, reg1, false); // big-endian
    view.setUint16(2, reg2, false); // big-endian

    return view.getFloat32(0, false); // big-endian
  }

  /**
   * Format register values for debugging (hex representation).
   */
  static formatRegistersHex(reg1: number, reg2: number): string {
    return `${reg1.toString(16).padStart(4, '0').toUpperCase()} ${reg2.toString(16).padStart(4, '0').toUpperCase()}`;
  }

  // ---------------------------------------------------------------------------
  // Public utility methods
  // ---------------------------------------------------------------------------

  /**
   * Read both temperature channels directly (bypasses cache).
   * Returns [BT temperature, ET temperature] in °C.
   */
  public async readTemperatures(): Promise<[number, number]> {
    try {
      const [btTemp, etTemp] = await Promise.all([
        this.readTemperatureRTU(YoshanRTUDevice.REGISTERS.BT_REGISTER),
        this.readTemperatureRTU(YoshanRTUDevice.REGISTERS.ET_REGISTER)
      ]);

      return [btTemp, etTemp];
    } catch (error) {
      logger.error(`Failed to read temperatures: ${error}`);
      throw error;
    }
  }

  /**
   * Get the current RTU connection status.
   */
  public async getConnectionStatus(): Promise<string> {
    try {
      const status = (await invoke('get_rtu_connection_status', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId
      })) as any;

      if (status.Connected !== undefined) {
        return 'Connected';
      } else if (status.Disconnected !== undefined) {
        return 'Disconnected';
      } else if (status.Error) {
        return `Error: ${status.Error}`;
      } else if (status.Reconnecting !== undefined) {
        return 'Reconnecting';
      } else {
        return 'Unknown';
      }
    } catch (error) {
      logger.error(`Failed to get RTU connection status: ${error}`);
      return 'Error';
    }
  }

  /**
   * List available serial ports.
   */
  public static async listAvailablePorts(): Promise<string[]> {
    try {
      const ports = (await invoke('list_available_ports')) as SerialPortInfo[];
      return ports.map((port) => port.port_name);
    } catch (error) {
      logger.error(`Failed to list serial ports: ${error}`);
      return [];
    }
  }

  /**
   * Set the port name for this device.
   */
  public setPortName(portName: string): void {
    this.portName = portName;
    logger.info(`Updated Yoshan port name to: ${portName}`);
  }

  /**
   * Set the slave ID for this device.
   */
  public setSlaveId(slaveId: number): void {
    this.slaveId = slaveId;
    logger.info(`Updated Yoshan slave ID to: ${slaveId}`);
  }

  /**
   * Update both port and slave ID at once.
   */
  public updateConfiguration(portName: string, slaveId: number): void {
    this.portName = portName;
    this.slaveId = slaveId;
    logger.info(`Updated Yoshan configuration - Port: ${portName}, Slave ID: ${slaveId}`);
  }

  public getPortName(): string {
    return this.portName;
  }

  public getSlaveId(): number {
    return this.slaveId;
  }

  public getConfiguration(): { portName: string; slaveId: number; baudRate: number } {
    return {
      portName: this.portName,
      slaveId: this.slaveId,
      baudRate: 9600
    };
  }

  /**
   * Start background monitoring for specific registers with custom interval.
   */
  public async startBackgroundMonitoring(
    registers?: number[],
    intervalMs?: number
  ): Promise<boolean> {
    try {
      const registersToMonitor = registers || [
        YoshanRTUDevice.REGISTERS.BT_REGISTER,
        YoshanRTUDevice.REGISTERS.BT_REGISTER + 1,
        YoshanRTUDevice.REGISTERS.ET_REGISTER,
        YoshanRTUDevice.REGISTERS.ET_REGISTER + 1
      ];

      await invoke('start_rtu_read_loop', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        registers: registersToMonitor,
        intervalMs: intervalMs || 2000
      });

      logger.info(
        `Started Yoshan background monitoring for registers: ${registersToMonitor.join(', ')} with ${intervalMs || 2000}ms interval`
      );
      return true;
    } catch (error) {
      logger.error(`Failed to start Yoshan background monitoring: ${error}`);
      return false;
    }
  }

  /**
   * Stop background monitoring.
   */
  public async stopBackgroundMonitoring(): Promise<boolean> {
    try {
      await invoke('stop_rtu_read_loop', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId
      });

      logger.info('Stopped Yoshan background monitoring');
      return true;
    } catch (error) {
      logger.error(`Failed to stop Yoshan background monitoring: ${error}`);
      return false;
    }
  }

  /**
   * Test connection by reading the bean temperature register.
   * If the device responds, the connection is good.
   */
  public async testConnection(): Promise<boolean> {
    try {
      const results: number[] = await invoke('read_rtu', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        reg: YoshanRTUDevice.REGISTERS.BT_REGISTER,
        count: 2
      });

      const temp = YoshanRTUDevice.convertToFloat(results[0], results[1]);

      if (modbusDebugEnabled.current === true) {
        logger.debug(
          `Yoshan connection test: register 0 raw=${YoshanRTUDevice.formatRegistersHex(results[0], results[1])}, float=${temp.toFixed(1)}°C`
        );
      }

      return true;
    } catch (error) {
      if (modbusDebugEnabled.current === true) {
        logger.debug(`Yoshan connection test failed: ${error}`);
      }
      return false;
    }
  }
}

export default YoshanRTUDevice;
